import { ModalViewModel } from '@/infra/ModalViewModel'
import { required, validationContext } from 'validx'
import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { unixEpoch } from '@/utils/date-util'

/**
 * View model for the dialog that changes a job's deadline.
 */
export class UpdateDeadlineDialogViewModel {
  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The new deadline.
   */
  @observable
  accessor deadline: Date

  /**
   * Validation context for the deadline.
   */
  readonly validation

  constructor(
    private readonly _job: Job,
    private readonly _jobStore: JobStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this.deadline = this._job.dateDeadline ?? unixEpoch()

    this.validation = validationContext(this, {
      deadline: [required('A deadline is required')],
    })
  }

  /**
   * Sets the new deadline.
   */
  @action.bound
  setDeadline(deadline: Date) {
    this.deadline = deadline
  }

  /**
   * Indicates if the deadline is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Saves the new deadline.
   */
  update = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying('Updating Deadline', 'Hang tight!')
      .show()

    try {
      await this._jobStore.updateDeadline(this._job.id, this.deadline)

      alert
        .withState('success')
        .saying('Success!', 'Deadline has been updated!')

      this.modal.close()
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Updating the deadline failed')(err)
    }
  })

  /**
   * Restores the job's current deadline.
   */
  @action.bound
  reset() {
    this.deadline = this._job.dateDeadline ?? unixEpoch()
    this.validation.reset()
  }
}
